import { Switch } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useRef, useState, useEffect } from "react";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import { updatePlace } from "store/slice/places/placeSlice";
import MDSnackbar from "components/MDSnackbar";
import { getPlaceById } from "store/slice/places/placeSlice";
import { setSelectedPlace } from "store/slice/places/placeSlice";
import { setSinglePlace } from "store/slice/places/placeSlice";

// Define the validation schema
const validationSchema = Yup.object().shape({
  site: Yup.string().when("enabled", {
    is: true,
    then: () => Yup.string().required("Site is required!"),
    otherwise: () => Yup.string(),
  }),
  apiKey: Yup.string().when("enabled", {
    is: true,
    then: () => Yup.string().required("Api Key is required!"),
    otherwise: () => Yup.string(),
  }),
  gracePeriod: Yup.number().when("enabled", {
    is: true,
    then: () =>
      Yup.number()
        .min(0, "Grace Period must be at least 0")
        .max(500, "Please enter value under 500!")
        .required("Grace Period is required!"),
    otherwise: () => Yup.number().min(0).max(500),
  }),
  exitGracePeriod: Yup.number().when("enabled", {
    is: true,
    then: () =>
      Yup.number()
        .min(0, "Exit Grace Period must be at least 0")
        .max(500, "Please enter value under 500!")
        .required("Exit Grace Period is required!"),
    otherwise: () => Yup.number().min(0).max(500),
  }),
});

function EnsightSettings() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const formikRef = useRef(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const placesData = useSelector((state) => state.places?.selectedPlace);
  const singlePlace = useSelector((state) => state.places?.singlePlace);
  let placeId =
    placesData?._id || new URLSearchParams(location?.search).get("placeId");

  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });

  useEffect(() => {
    getPlace();
  }, [placeId]);

  useEffect(() => {
    if (singlePlace?.ensightSettings) {
      formikRef?.current?.setFieldValue(
        "enabled",
        singlePlace.ensightSettings?.enabled || false
      );
      formikRef?.current?.setFieldValue(
        "site",
        singlePlace.ensightSettings?.site || ""
      );
      formikRef?.current?.setFieldValue(
        "apiKey",
        singlePlace.ensightSettings?.apiKey || ""
      );
      formikRef?.current?.setFieldValue(
        "gracePeriod",
        singlePlace.ensightSettings?.gracePeriod || 30
      );
      formikRef?.current?.setFieldValue(
        "exitGracePeriod",
        singlePlace.ensightSettings?.exitGracePeriod || 15
      );
    }
  }, [singlePlace]);

  function getPlace() {
    if (singlePlace?.ensightSettings) {
      formikRef?.current?.setFieldValue(
        "enabled",
        singlePlace.ensightSettings?.enabled || false
      );
      formikRef?.current?.setFieldValue(
        "site",
        singlePlace.ensightSettings?.site || ""
      );
      formikRef?.current?.setFieldValue(
        "apiKey",
        singlePlace.ensightSettings?.apiKey || ""
      );
      formikRef?.current?.setFieldValue(
        "gracePeriod",
        singlePlace.ensightSettings?.gracePeriod || 30
      );
      formikRef?.current?.setFieldValue(
        "exitGracePeriod",
        singlePlace.ensightSettings?.exitGracePeriod || 15
      );
    } else {
      dispatch(getPlaceById(placeId))
        .unwrap()
        .then((res) => {
          if (res?.success) {
            dispatch(setSinglePlace(res?.place));
            formikRef?.current?.setFieldValue(
              "enabled",
              res?.place?.ensightSettings?.enabled || false
            );
            formikRef?.current?.setFieldValue(
              "site",
              res?.place?.ensightSettings?.site || ""
            );
            formikRef?.current?.setFieldValue(
              "apiKey",
              res?.place?.ensightSettings?.apiKey || ""
            );
            formikRef?.current?.setFieldValue(
              "gracePeriod",
              res?.place?.ensightSettings?.gracePeriod || 30
            );
            formikRef?.current?.setFieldValue(
              "exitGracePeriod",
              res?.place?.ensightSettings?.exitGracePeriod || 15
            );
          }
        });
    }
  }

  const handleEnsightSettingsUpdate = (value) => {
    const type = "UPDATE";
    const ensightSettings = {
      enabled: value.enabled,
      site: value.site,
      apiKey: value.apiKey,
      gracePeriod: value.gracePeriod,
      exitGracePeriod: value.exitGracePeriod,
    };

    dispatch(updatePlace({ placeId, type, ensightSettings }))
      .unwrap()
      .then((res) => {
        const success = res?.success;
        if (success) {
          dispatch(getPlaceById(placeId))
            .unwrap()
            .then((res) => {
              if (res?.success) {
                dispatch(setSelectedPlace(res?.place));
                dispatch(setSinglePlace(res?.place));
              }
            });
        }
        setNotification({
          ...notification,
          color: success ? "success" : "error",
          title: success ? "Success" : "Error",
          content: res?.message || "Settings updated successfully",
          icon: success ? "check" : "warning",
          show: true,
        });
      })
      .catch((err) => {
        console.error("Error updating settings:", err);
        setNotification({
          ...notification,
          color: "error",
          title: "Error",
          content: err?.message || "Failed to update settings",
          icon: "warning",
          show: true,
        });
      });
  };

  return (
    <MDBox>
      <MDBox pt={2} pb={3}>
        <Formik
          innerRef={formikRef}
          initialValues={{
            enabled: false,
            site: "",
            apiKey: "",
            gracePeriod: 30,
            exitGracePeriod: 15,
          }}
          validationSchema={validationSchema}
          onSubmit={(value) => {
            handleEnsightSettingsUpdate(value);
          }}
        >
          {(props) => (
            <form onSubmit={props.handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Grid className="mb-3">
                    <MDTypography
                      variant="subtitle2"
                      style={{ minWidth: "120px" }}
                    >
                      Site Id:
                    </MDTypography>
                    <MDInput
                      inputProps={{
                        type: "string",
                      }}
                      name="site"
                      required={props.values.enabled}
                      value={props.values.site}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      error={props.errors.site && props.touched.site}
                      success={!props.errors.site && props.touched.site}
                      helperText={
                        props.errors.site &&
                        props.touched.site &&
                        props.errors.site
                      }
                    />
                  </Grid>

                  <Grid className="mb-3">
                    <MDTypography
                      variant="subtitle2"
                      style={{ minWidth: "120px" }}
                    >
                      API Key:
                    </MDTypography>
                    <MDInput
                      inputProps={{
                        type: "string",
                      }}
                      name="apiKey"
                      required={props.values.enabled}
                      value={props.values.apiKey}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      error={props.errors.apiKey && props.touched.apiKey}
                      success={!props.errors.apiKey && props.touched.apiKey}
                      helperText={
                        props.errors.apiKey &&
                        props.touched.apiKey &&
                        props.errors.apiKey
                      }
                    />
                  </Grid>

                  <Grid className="mb-3">
                    <MDTypography
                      variant="subtitle2"
                      style={{ minWidth: "120px" }}
                    >
                      Entry Grace Period #In Minutes:
                    </MDTypography>
                    <MDInput
                      inputProps={{
                        type: "number",
                        min: 0,
                        max: 500,
                      }}
                      name="gracePeriod"
                      required={props.values.enabled}
                      value={props.values.gracePeriod}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      error={
                        props.errors.gracePeriod && props.touched.gracePeriod
                      }
                      success={
                        !props.errors.gracePeriod && props.touched.gracePeriod
                      }
                      helperText={
                        props.errors.gracePeriod &&
                        props.touched.gracePeriod &&
                        props.errors.gracePeriod
                      }
                    />
                  </Grid>

                  <Grid className="mb-3">
                    <MDTypography
                      variant="subtitle2"
                      style={{ minWidth: "120px" }}
                    >
                      Exit Grace Period #In Minutes:
                    </MDTypography>
                    <MDInput
                      inputProps={{
                        type: "number",
                        min: 0,
                        max: 500,
                      }}
                      name="exitGracePeriod"
                      required={props.values.enabled}
                      value={props.values.exitGracePeriod}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      error={
                        props.errors.exitGracePeriod &&
                        props.touched.exitGracePeriod
                      }
                      success={
                        !props.errors.exitGracePeriod &&
                        props.touched.exitGracePeriod
                      }
                      helperText={
                        props.errors.exitGracePeriod &&
                        props.touched.exitGracePeriod &&
                        props.errors.exitGracePeriod
                      }
                    />
                  </Grid>
                </Grid>

                <Grid item xs={6}>
                  <MDBox
                    pt={2}
                    display="flex"
                    justifyContent="end"
                    alignItems="center"
                  >
                    <MDTypography
                      variant="subtitle2"
                      style={{ minWidth: "120px" }}
                    >
                      Enabled:
                    </MDTypography>
                    <Switch
                      checked={props.values.enabled}
                      onChange={(e) => {
                        props.setFieldValue("enabled", e.target.checked);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </MDBox>
                </Grid>

                <Grid item xs={12}>
                  <MDBox pt={2} display="flex" justifyContent="end">
                    <MDButton
                      color={sidenavColor}
                      variant="contained"
                      type="submit"
                    >
                      Save
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </MDBox>
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
    </MDBox>
  );
}

export default EnsightSettings;
